import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRANDS, branchesFor, ageFromDob, ageBand, ageBandLabel } from "@/lib/brands";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, ArrowLeft, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";

const initial = {
  pet_name: "", species: "Dog", breed: "", age: "", sex: "", weight: "", colour: "",
  microchip_number: "", owner_name: "", phone: "", email: "", address: "", notes: "",
  photo_file_id: null, brand: "", owning_branch: "", dob: "",
};

export default function PatientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/patients/${id}`).then((r) => { setData({ ...initial, ...r.data }); setLoading(false); });
  }, [id]);

  const onChange = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: f } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, photo_file_id: f.id }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!id && user?.role === "admin" && (!data.brand || !data.owning_branch)) {
      toast.error("Select a brand and branch for this patient");
      return;
    }
    setBusy(true);
    try {
      const res = id
        ? await api.put(`/patients/${id}`, data)
        : await api.post("/patients", data);
      toast.success(`Patient ${id ? "updated" : "created"} — ${res.data.case_number}`);
      navigate(`/patients/${res.data.id}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-[1100px] mx-auto animate-fade-in">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">{id ? "Edit Patient" : "New Patient"}</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">Permanent pet record. Each visit creates a new consultation linked to this patient.</p>

      <form onSubmit={submit} className="space-y-6">
        {user?.role === "admin" && !id && (
          <Card className="p-6 border-border/60">
            <h2 className="font-primary text-lg font-semibold mb-4">Brand &amp; Branch</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Brand (Store) <span className="text-destructive">*</span></Label>
                <Select value={data.brand} onValueChange={(v) => setData((d) => ({ ...d, brand: v, owning_branch: "" }))}>
                  <SelectTrigger data-testid="patient-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
                  <SelectContent>{BRANDS.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Owning Branch <span className="text-destructive">*</span></Label>
                <Select value={data.owning_branch} onValueChange={(v) => setData((d) => ({ ...d, owning_branch: v }))} disabled={!data.brand}>
                  <SelectTrigger data-testid="patient-owning-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branchesFor(data.brand).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 border-border/60">
          <h2 className="font-primary text-lg font-semibold mb-4">Pet</h2>
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex flex-col items-center gap-2 shrink-0">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="patient-photo-input" onChange={onPhoto} />
              {data.photo_file_id ? (
                <div className="relative group">
                  <img src={fileUrl(data.photo_file_id)} alt="pet" data-testid="patient-photo-preview"
                       className="w-32 h-32 rounded-xl object-cover border border-border" />
                  <button type="button" data-testid="remove-patient-photo-btn"
                          onClick={() => setData((d) => ({ ...d, photo_file_id: null }))}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button type="button" data-testid="upload-patient-photo-btn"
                        onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="w-32 h-32 rounded-xl border-2 border-dashed border-border bg-muted/40 hover:bg-muted transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground">
                  {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <UploadCloud className="h-7 w-7" />}
                  <span className="text-xs">{uploading ? "Uploading…" : "Pet Photo"}</span>
                </button>
              )}
              {data.photo_file_id && (
                <Button type="button" variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? "Uploading…" : "Change"}
                </Button>
              )}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
            <div className="space-y-1.5">
              <Label htmlFor="pet_name">Pet Name <span className="text-destructive">*</span></Label>
              <Input id="pet_name" data-testid="patient-pet_name" value={data.pet_name} onChange={onChange("pet_name")} required />
            </div>
            <div className="space-y-1.5">
              <Label>Species</Label>
              <Select value={data.species} onValueChange={(v) => setData((d) => ({ ...d, species: v }))}>
                <SelectTrigger data-testid="patient-species"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Dog","Cat","Bird","Rabbit","Hamster","Reptile","Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label htmlFor="breed">Breed</Label>
              <Input id="breed" data-testid="patient-breed" value={data.breed} onChange={onChange("breed")} /></div>
            <div className="space-y-1.5"><Label htmlFor="dob">Date of Birth</Label>
              <Input id="dob" type="date" data-testid="patient-dob" value={data.dob || ""} onChange={onChange("dob")} />
              {data.dob && <p className="text-xs text-muted-foreground">Age: <span className="font-medium text-foreground">{ageFromDob(data.dob)}</span> · {ageBandLabel[ageBand(data.dob)]}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Sex</Label>
              <Select value={data.sex || ""} onValueChange={(v) => setData((d) => ({ ...d, sex: v }))}>
                <SelectTrigger data-testid="patient-sex"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {["Male","Female","Male (neutered)","Female (spayed)"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label htmlFor="weight">Weight (kg)</Label>
              <Input id="weight" data-testid="patient-weight" value={data.weight} onChange={onChange("weight")} /></div>
            <div className="space-y-1.5"><Label htmlFor="colour">Colour</Label>
              <Input id="colour" data-testid="patient-colour" value={data.colour} onChange={onChange("colour")} /></div>
            <div className="space-y-1.5"><Label htmlFor="microchip_number">Microchip Number (optional)</Label>
              <Input id="microchip_number" data-testid="patient-microchip_number" value={data.microchip_number} onChange={onChange("microchip_number")} /></div>
          </div>
          </div>
        </Card>

        <Card className="p-6 border-border/60">
          <h2 className="font-primary text-lg font-semibold mb-4">Owner</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label htmlFor="owner_name">Owner Name <span className="text-destructive">*</span></Label>
              <Input id="owner_name" data-testid="patient-owner_name" value={data.owner_name} onChange={onChange("owner_name")} required /></div>
            <div className="space-y-1.5"><Label htmlFor="phone">Phone Number <span className="text-destructive">*</span></Label>
              <Input id="phone" data-testid="patient-phone" type="tel" value={data.phone} onChange={onChange("phone")} required /></div>
            <div className="space-y-1.5"><Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="patient-email" type="email" value={data.email} onChange={onChange("email")} /></div>
            <div className="md:col-span-2 lg:col-span-3 space-y-1.5">
              <Label htmlFor="address">Address</Label>
              <Textarea id="address" data-testid="patient-address" value={data.address || ""} onChange={onChange("address")} rows={2} />
            </div>
            <div className="md:col-span-2 lg:col-span-3 space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" data-testid="patient-notes" value={data.notes || ""} onChange={onChange("notes")} rows={2} />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button data-testid="save-patient-btn" type="submit" disabled={busy} className="font-semibold">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {id ? "Update Patient" : "Create Patient"}
          </Button>
        </div>
      </form>
    </div>
  );
}
